import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';

import { getTopContent, TopContentSource } from '@/features/universal-search/services/topContent';

interface TopContentContextValue {
  topContent: TopContentSource | null;
  isLoading: boolean;
  error: boolean;
}

const TopContentContext = createContext<TopContentContextValue | undefined>(undefined);

interface TopContentProviderProps {
  children: ReactNode;
}

export const TopContentProvider: FC<TopContentProviderProps> = ({ children }) => {
  const [topContent, setTopContent] = useState<TopContentSource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchTopContent = async () => {
      setIsLoading(true);
      setError(false);

      try {
        const data = await getTopContent();
        setTopContent(data);
      } catch (err) {
        console.error('Error loading top content:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopContent();
  }, []);

  return (
    <TopContentContext.Provider value={{ topContent, isLoading, error }}>
      {children}
    </TopContentContext.Provider>
  );
};

export const useTopContent = (): TopContentContextValue => {
  const context = useContext(TopContentContext);

  if (context === undefined) {
    throw new Error('useTopContent must be used within a TopContentProvider');
  }

  return context;
};
